import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/user/_layout/")({
  component: UserIndex,
});

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { lazy, Suspense } from "react";

/** recharts (and its d3/redux dependency tree) is the heaviest thing the
 * dashboard pulls in, so it is fetched alongside the chart rather than in the
 * entry bundle. */
const ChartBarUser = lazy(() =>
  import("@/components/user/user-dashboard/UserChart").then((m) => ({
    default: m.ChartBarUser,
  })),
);
import useTodayStatus from "@/hooks/api/attendance/useTodayStatus";
import type { DayStatus } from "@/types/timesheet";
import { StatCard } from "@/components/reusable-components/stat-card";
import { SwipableCard } from "@/components/reusable-components/swipable-card";
import useMyAttendanceSummary from "@/hooks/api/attendance-summary/useMyAttendanceSummary";
import { shiftAverageItems, shiftHoursItems } from "@/lib/attendance-summary";
import {
  endOfOrgMonth,
  formatToOrgDate,
  startOfOrgMonth,
} from "@/lib/date-utils";
import { Skeleton } from "@/components/ui/skeleton";
import { isSupervisorUser } from "@/lib/supervisor";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { SupervisorAccessBanner } from "@/components/supervisor/dashboard/SupervisorAccessBanner";
import { MissingCheckoutAlert } from "@/components/user/user-dashboard/MissingCheckoutAlert";
import { useFeatureStore } from "@/stores/useFeatureStore";
import PageHeader from "@/components/reusable-components/header/page-header";

/** Only a present day is highlighted; an absence is called out, and the
 * remaining states are neutral facts about the day rather than warnings. */
const DAY_STATUS_DISPLAY = {
  PRESENT: { label: "Present", className: "text-primary" },
  ABSENT: { label: "Absent", className: "text-destructive" },
  ON_LEAVE: { label: "On Leave", className: "text-muted-foreground" },
  HALF_LEAVE: { label: "Half Leave", className: "text-muted-foreground" },
  HOLIDAY: { label: "Holiday", className: "text-muted-foreground" },
  OFF_DAY: { label: "Off Day", className: "text-muted-foreground" },
  PENDING: {
    label: "Not checked in yet",
    className: "text-muted-foreground",
  },
  // Nobody rostered the day: claim nothing rather than invent a status.
  NOT_SCHEDULED: { label: "-", className: "text-muted-foreground" },
} satisfies Record<DayStatus, { label: string; className: string }>;

function UserIndex() {
  // Every widget here is attendance data; with attendance off (for the org or
  // this role) mount none of them, so none of their requests hit the gate.
  const attendanceEnabled = useFeatureStore().isEnabled("attendance");
  return attendanceEnabled ? <AttendanceDashboard /> : <NoAttendanceDashboard />;
}

function NoAttendanceDashboard() {
  const { data: capabilities } = useMyPermissions();

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="User Dashboard"
          description="Get a complete overview of your month at work."
        />
        {/* Shown whenever leave or overtime work is waiting, whatever the
            approval settings say now (T-165). */}
        {isSupervisorUser(capabilities) && <SupervisorAccessBanner />}
        <Card>
          <CardHeader>
            <CardTitle>Welcome</CardTitle>
            <CardDescription>
              Use the sidebar to open the modules available to you.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    </SidebarInset>
  );
}

function AttendanceDashboard() {
  const { data: capabilities } = useMyPermissions();

  // Today's day status — Present / Absent / Off Day / Holiday / leave.
  const todayStatusQuery = useTodayStatus();
  const todayStatusLoading = todayStatusQuery.isPending;
  const todayStatus = todayStatusQuery.data
    ? DAY_STATUS_DISPLAY[todayStatusQuery.data.status]
    : null;

  //TODO: Integrate pie chart data from backend

  // The stat cards report the current calendar month. The chart below keeps its
  // own weekly window and fetches for itself, so the two ranges stay separate.
  const monthStart = startOfOrgMonth();
  // Both the label and the range below follow the user's AD/BS preference, so a
  // BS month name always names the period the numbers actually cover.
  const monthLabel = formatToOrgDate(monthStart, "MMMM");

  //Summary Data for the cards on initial render
  const myAttendanceSummaryQuery = useMyAttendanceSummary({
    start_date: monthStart.format("YYYY-MM-DD"),
    end_date: endOfOrgMonth().format("YYYY-MM-DD"),
  });
  const myAttendanceSummary = myAttendanceSummaryQuery.data ?? null;
  const myAttendanceSummaryLoading = myAttendanceSummaryQuery.isPending;

  return (
    <SidebarInset className="xl:h-svh xl:overflow-hidden">
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0 xl:flex-1 xl:min-h-0 xl:overflow-hidden">
        <PageHeader
          title="User Dashboard"
          description="Get a complete overview of your month at work."
        />

        <div className="flex flex-col lg:flex-row gap-4">
          <MissingCheckoutAlert />
          {isSupervisorUser(capabilities) && <SupervisorAccessBanner />}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 xl:flex-1 xl:min-h-0 xl:grid-rows-[auto_1fr]">
          {/* Row 1: Check-in Status | Total Hours Worked | Present Days */}
          <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 sm:grid-cols-3 gap-4 *:data-[slot=card]:bg-linear-to-t *:data-[slot=card]:shadow-xs col-span-1 xl:col-span-3">
            <Card className="@container/card">
              <CardHeader>
                <CardDescription>Today's Status</CardDescription>
                <CardTitle
                  className={`text-2xl font-semibold tabular-nums @[250px]/card:text-3xl ${
                    todayStatus?.className ?? "text-muted-foreground"
                  }`}
                >
                  {todayStatusLoading ? (
                    <Skeleton className="h-[2.2rem] w-30" />
                  ) : (
                    (todayStatus?.label ?? "—")
                  )}
                </CardTitle>
              </CardHeader>
            </Card>

            {/* Shift-wise, like the two average cards below: a split-shift
             * or mixed-roster month shows which shift the hours came from,
             * and a single-shift user sees a plain stat card as before. */}
            <SwipableCard
              label="Total Hours Worked"
              qualifier={monthLabel}
              items={shiftHoursItems(myAttendanceSummary?.shift_averages)}
              loading={myAttendanceSummaryLoading}
            />

            <StatCard
              label={`Present Days (${monthLabel})`}
              value={myAttendanceSummary?.present_days ?? ""}
              loading={myAttendanceSummaryLoading}
            />
          </div>

          {/* Row 2: Chart (2 cols) + Stats (1 col) — fills remaining height on xl+ */}
          <div className="col-span-1 xl:col-span-2 xl:min-h-0">
            <Suspense fallback={<Skeleton className="h-full min-h-[300px] w-full rounded-xl" />}>
              <ChartBarUser />
            </Suspense>
          </div>

          <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid gap-4 *:data-[slot=card]:bg-linear-to-t *:data-[slot=card]:shadow-xs col-span-1 xl:grid-rows-3 xl:min-h-0">
            <SwipableCard
              label={"Average Check-in"}
              qualifier={monthLabel}
              items={shiftAverageItems(
                myAttendanceSummary?.shift_averages,
                "average_checkin_time"
              )}
              loading={myAttendanceSummaryLoading}
              className="xl:h-full"
            />
            <SwipableCard
              label={"Average Check-out"}
              qualifier={monthLabel}
              items={shiftAverageItems(
                myAttendanceSummary?.shift_averages,
                "average_checkout_time"
              )}
              loading={myAttendanceSummaryLoading}
              className="xl:h-full"
            />
            <StatCard
              label={`Attendance Rate (${monthLabel})`}
              value={
                myAttendanceSummary?.attendance_rate != null
                  ? `${myAttendanceSummary.attendance_rate.toFixed(1)}%`
                  : ""
              }
              loading={myAttendanceSummaryLoading}
              className="xl:h-full"
            />
          </div>
        </div>
      </div>
    </SidebarInset>
  );
}
